/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { action, observable } from "mobx";

/*
 * Light / dark, shared by every Tech9IQ module: the person's choice is the `t9_theme` cookie
 * (light | dark | system) on .tech9iq.com, mirrored in localStorage for local development. Missing
 * or "system" follows the operating system. The page shows it as <html data-theme="light|dark">,
 * which index.html's inline script sets before the first paint; this store keeps it current
 * (the OS setting changing, another module changing the cookie) and writes the cookie when the
 * person picks a theme in the profile menu.
 */
export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_COOKIE = "t9_theme";
const ONE_YEAR = 31536000;

const isPreference = (v: unknown): v is ThemePreference =>
  v === "light" || v === "dark" || v === "system";

const readCookie = (): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)t9_theme=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const readStorage = (): string | null => {
  try {
    return window.localStorage.getItem(THEME_COOKIE);
  } catch (e) {
    return null;
  }
};

/** The stored choice: the cookie first (shared across *.tech9iq.com), then localStorage. */
export const readPreference = (): ThemePreference => {
  const cookie = readCookie();
  if (isPreference(cookie)) return cookie;
  const stored = readStorage();
  return isPreference(stored) ? stored : "system";
};

const systemDark = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export const resolveTheme = (pref: ThemePreference): ResolvedTheme =>
  pref === "system" ? (systemDark() ? "dark" : "light") : pref;

/** `Domain=.tech9iq.com` on tech9iq.com hosts (so every module shares it); none on localhost. */
export const cookieAttributes = (hostname: string, https: boolean): string => {
  const parts = ["Path=/", `Max-Age=${ONE_YEAR}`, "SameSite=Lax"];
  if (/(^|\.)tech9iq\.com$/i.test(hostname)) parts.push("Domain=.tech9iq.com");
  if (https) parts.push("Secure");
  return parts.join("; ");
};

const writePreference = (pref: ThemePreference) => {
  try {
    document.cookie = `${THEME_COOKIE}=${pref}; ${cookieAttributes(
      window.location.hostname,
      window.location.protocol === "https:",
    )}`;
  } catch (e) {
    // cookies blocked: localStorage still remembers it here
  }
  try {
    window.localStorage.setItem(THEME_COOKIE, pref);
  } catch (e) {
    // storage blocked: applied for this page only
  }
};

class ThemeModeStore {
  @observable preference: ThemePreference = "system";

  @observable resolved: ResolvedTheme = "light";

  private started = false;

  /** Reads the choice, applies it and keeps it current. Safe to call more than once. */
  @action start() {
    this.sync();
    if (this.started || typeof window === "undefined") return;
    this.started = true;
    if (typeof window.matchMedia === "function") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => this.sync();
      if (typeof mq.addEventListener === "function") mq.addEventListener("change", onChange);
      else if (typeof (mq as any).addListener === "function") (mq as any).addListener(onChange);
    }
    // Another module (or tab) may have changed the shared cookie.
    window.addEventListener("focus", () => this.sync());
    window.addEventListener("storage", e => {
      if (e.key === THEME_COOKIE) this.sync();
    });
  }

  @action sync() {
    this.preference = readPreference();
    this.apply();
  }

  @action set(pref: ThemePreference) {
    this.preference = pref;
    writePreference(pref);
    this.apply();
  }

  @action private apply() {
    this.resolved = resolveTheme(this.preference);
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.setAttribute("data-theme", this.resolved);
    root.style.colorScheme = this.resolved;
  }
}

const themeMode = new ThemeModeStore();

/**
 * The value of a CSS colour token for code that cannot use var() (canvas): "var(--x, #fallback)"
 * becomes the token's current value; anything else is returned as it is.
 */
export const cssColor = (value: string): string => {
  const m = /^var\((--[\w-]+)(?:,\s*([^)]+))?\)$/.exec(value.trim());
  if (!m || typeof document === "undefined") return value;
  const v = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
  return v || (m[2] ? m[2].trim() : value);
};

export default themeMode;
